import {div, filler, span} from '@xh/hoist/cmp/layout';
import {hoistCmp, HoistProps, uses} from '@xh/hoist/core';
import {button} from '@xh/hoist/desktop/cmp/button';
import {select} from '@xh/hoist/desktop/cmp/input';
import {panel} from '@xh/hoist/desktop/cmp/panel';
import {toolbar} from '@xh/hoist/desktop/cmp/toolbar';
import {Icon} from '@xh/hoist/icon';
import {dialog} from '@xh/hoist/kit/blueprint';
import {isEmpty, isNil, startCase} from 'lodash';
import {styleClassNames} from '../GridSpecBuilder';
import {CELL_STYLE_VALUES, CellStyle, StylePart, StyleRule} from '../GridSpecTypes';
import {conditionRow, configPanelItem, OPERATOR_OPTIONS, sectionAddButton, stackedPanel} from './Common';
import {GridSpecConfiguratorModel, StyleEntry, StyleRuleScope} from './GridSpecConfiguratorModel';

const STYLE_PART_LABELS: Record<StylePart, string> = {
    fontWeight: 'Font Weight',
    fontStyle: 'Font Style',
    color: 'Text Color',
    background: 'Background'
};

//------------------------
// Rule list
//------------------------
interface StyleRulesPanelProps extends HoistProps<GridSpecConfiguratorModel> {
    scope: StyleRuleScope;
    title: string;
}

/** A section listing the row or cell style rules as cards - add and edit them in the rule dialog. */
export const styleRulesPanel = hoistCmp.factory<StyleRulesPanelProps>({
    model: uses(GridSpecConfiguratorModel),
    render({model, scope, title}) {
        const rules = model.rulesFor(scope);

        return stackedPanel({
            title,
            headerItems: [sectionAddButton(() => model.openRuleDialog(scope), `Add ${scope} style`)],
            items: [
                ...rules.map((rule, idx) =>
                    styleRuleCard({
                        key: `${scope}-rule-${idx}`,
                        rule,
                        onEdit: () => model.openRuleDialog(scope, idx),
                        onRemove: () => model.removeRule(scope, idx)
                    })
                ),
                div({
                    className: 'gs-configurator__muted gs-rules__hint',
                    omit: !isEmpty(rules),
                    item: `No ${scope} styles.`
                })
            ]
        });
    }
});

interface StyleRuleCardProps extends HoistProps<GridSpecConfiguratorModel> {
    rule: StyleRule;
    onEdit: () => void;
    onRemove: () => void;
}

const styleRuleCard = hoistCmp.factory<StyleRuleCardProps>(({model, rule, onEdit, onRemove}) =>
    configPanelItem({
        className: 'config-panel-item gs-rules__card',
        omitFiller: true,
        items: [
            span({className: 'gs-rules__name', item: describeRule(rule, model)}),
            filler(),
            stylePreview({style: rule.style}),
            button({icon: Icon.gear(), minimal: true, title: 'Edit style', onClick: onEdit}),
            button({icon: Icon.delete(), intent: 'danger', minimal: true, title: 'Remove style', onClick: onRemove})
        ]
    })
);

function describeRule({when}: StyleRule, model: GridSpecConfiguratorModel): string {
    const op = OPERATOR_OPTIONS.find(o => o.value === when.op)?.label ?? when.op,
        value = isNil(when.value) || when.value === '' ? '…' : when.value;
    return `${model.specModel.fieldLabel(when.field)} ${op} ${value}`;
}

interface StylePreviewProps extends HoistProps {
    style: CellStyle;
}

const stylePreview = hoistCmp.factory<StylePreviewProps>({
    model: false,
    render: ({style}) => div({className: ['gs-rules__preview', ...styleClassNames(style)].join(' '), item: 'Abc'})
});

//------------------------
// Rule dialog
//------------------------
/** Edits one rule - its condition and one or more styles, saved by `saveRuleDialog()`. */
export const styleRuleDialog = hoistCmp.factory<GridSpecConfiguratorModel>({
    model: uses(GridSpecConfiguratorModel),
    render({model}) {
        const {ruleScope: scope, ruleIndex, ruleCondition, ruleStyles} = model;
        if (!scope) return null;

        const style = Object.fromEntries(ruleStyles.filter(s => s.value).map(s => [s.part, s.value])) as CellStyle;

        return dialog({
            title: `${isNil(ruleIndex) ? 'Add' : 'Edit'} ${scope === 'row' ? 'Row' : 'Cell'} Style`,
            icon: Icon.gear(),
            isOpen: true,
            style: {width: 420},
            canOutsideClickClose: false,
            onClose: () => model.closeRuleDialog(),
            item: panel({
                className: 'gs-rule-dialog',
                items: [
                    stackedPanel({
                        title: scope === 'row' ? 'Style rows when' : 'Style cells when',
                        item: conditionRow({
                            condition: ruleCondition,
                            fieldOptions: model.fieldOptions,
                            onChange: changes => model.updateRuleCondition(changes)
                        })
                    }),
                    stackedPanel({
                        title: 'Styles',
                        headerItems: [sectionAddButton(() => model.addRuleStyle(), 'Add style', isEmpty(model.unusedStyleParts))],
                        items: [
                            ...ruleStyles.map((entry, idx) => styleEntryRow({key: `style-${idx}`, entry, index: idx})),
                            div({
                                className: 'gs-configurator__muted gs-rules__hint',
                                omit: !isEmpty(ruleStyles),
                                item: 'No styles - add one with +.'
                            }),
                            configPanelItem({label: 'Preview', item: stylePreview({style})})
                        ]
                    })
                ],
                bbar: toolbar(
                    filler(),
                    button({text: 'Cancel', onClick: () => model.closeRuleDialog()}),
                    button({text: 'Save', intent: 'success', disabled: !model.canSaveRule, onClick: () => model.saveRuleDialog()})
                )
            })
        });
    }
});

interface StyleEntryRowProps extends HoistProps<GridSpecConfiguratorModel> {
    entry: StyleEntry;
    index: number;
}

/** A style type and its value - the value options depend on the type. */
const styleEntryRow = hoistCmp.factory<StyleEntryRowProps>(({model, entry, index}) => {
    const {part, value} = entry,
        swatch = part === 'color' ? 'color' : part === 'background' ? 'bg' : null,
        swatchEl = (v: string) => span({className: `gs-swatch gs-swatch--${swatch}-${v}`}),
        renderer = swatch ? opt => span(swatchEl(opt.value), opt.label) : undefined;

    return configPanelItem({
        className: 'config-panel-item config-panel-item--condition',
        omitFiller: true,
        items: [
            select({
                value: part,
                onChange: p => model.updateRuleStyle(index, {part: p}),
                options: [part, ...model.unusedStyleParts].map(p => ({value: p, label: STYLE_PART_LABELS[p]})),
                enableFilter: false,
                width: 130
            }),
            select({
                value,
                onChange: v => model.updateRuleStyle(index, {value: v}),
                options: CELL_STYLE_VALUES[part].map(v => ({value: v, label: startCase(v)})),
                optionRenderer: renderer,
                valueRenderer: renderer,
                placeholder: 'Select...',
                enableFilter: false,
                flex: 1,
                width: null
            }),
            button({icon: Icon.x(), intent: 'danger', minimal: true, title: 'Remove style', onClick: () => model.removeRuleStyle(index)})
        ]
    });
});
